import { createFileRoute } from "@tanstack/react-router";
import { Link as RouterLink } from "@tanstack/react-router";
import { ArrowRight, Link2, Mail, QrCode, UserPlus, Wallet, Lock, Signature } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/sharing-hero-tools.webp";
import walletImg from "@/assets/sharing-wallet.webp";
import leadImg from "@/assets/sharing-lead-capture.webp";

const IMG: Record<string, string> = { hero: heroImg, wallet: walletImg, lead: leadImg };

export const Route = createFileRoute("/vi/platform/sharing")({
  head: () => ({
    meta: [
      { title: "Công cụ chia sẻ & networking — banana.cards" },
      { name: "description", content: "NFC, QR, link, Wallet pass và signature email. Một hồ sơ, mọi kênh chia sẻ." },
      { property: "og:title", content: "Công cụ chia sẻ & networking — banana.cards" },
      { property: "og:description", content: "NFC, QR, link, Wallet pass và signature email." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/sharing", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Công cụ chia sẻ & networking"
      title="Chia sẻ bạn là ai,"
      italic="ở bất cứ đâu."
      description="Trao đổi liên hệ qua NFC, QR, link, Wallet pass và signature email. Một hồ sơ, vô số cách chia sẻ — trên mọi thiết bị."
      heroImage={IMG['hero']}
      metrics={[{ v: "9", l: "kênh chia sẻ" }, { v: "0", l: "cần cài app" }, { v: "100%", l: "đa nền tảng" }]}
      bullets={[
        { icon: QrCode, title: "QR động", desc: "Cập nhật đích đến bất kỳ lúc nào không cần in lại. Theo dõi scan, thiết bị và vị trí." },
        { icon: Link2, title: "Short link", desc: "URL ngắn tùy chỉnh với analytics — hoàn hảo cho bio link và campaign." },
        { icon: Mail, title: "Signature email", desc: "Đưa thẻ vào mọi email với builder signature một dòng." }
      ]}
      deepDives={[
        { eyebrow: "Wallet", title: "Thêm vào Apple & Google", italic: "Wallet trong 1 chạm.", description: "Người nhận lưu thẻ của bạn vào wallet điện thoại để truy cập tức thì — không app, không cuộn message.", image: IMG["wallet"], reverse: false, points: ["Apple Wallet pass tự cập nhật", "Google Wallet pass", "Cập nhật live khi đổi hồ sơ", "Truy cập từ lock screen"] },
        { eyebrow: "Sự kiện", title: "Lead được capture,", italic: "tự sắp xếp.", description: "Dùng QR riêng cho mỗi sự kiện để capture lead tại expo, conference. Mỗi scan vào CRM với sự kiện được tag tự động.", image: IMG["lead"], reverse: true, points: ["QR theo sự kiện", "Auto-tag trong CRM", "Form capture tùy chỉnh", "Export CSV & Zapier"] }
      ]}
      howItWorks={[
        { title: "Tạo cách chia sẻ", desc: "NFC tap, QR, link, signature hoặc Wallet." },
        { title: "Người nhận nhận", desc: "Hồ sơ mở trong browser tức thì." },
        { title: "Họ tap save", desc: "Thẻ vào contacts hoặc wallet." },
        { title: "Luôn đồng bộ", desc: "Cập nhật của bạn đẩy sang bản đã lưu." }
      ]}
      capabilities={["QR không giới hạn", "Short link branded", "Apple & Google Wallet", "Builder signature email", "Tracking theo sự kiện", "Analytics mỗi kênh", "Geo & device insights", "UTM passthrough"]}
      faq={[
        { q: "Có bao nhiêu cách chia sẻ thẻ Banana?", a: "Ít nhất 7: NFC tap, QR động, link ngắn, Apple/Google Wallet pass, block signature email, AirDrop / native share, gửi qua WhatsApp/iMessage/LinkedIn/SMS." },
        { q: "Có theo dõi được ai xem thẻ không?", a: "Có — mỗi view, tap và scan được log với timestamp, device, browser, vị trí và nguồn." },
        { q: "Có thể đổi thẻ không cần in lại?", a: "Có — mọi cách chia sẻ trỏ về hồ sơ live. Cập nhật một lần, mọi QR, NFC, Wallet pass và signature phản ánh ngay." },
        { q: "Người nhận có cần app để save không?", a: "Không — thẻ có nút 'Save contact' thêm vCard trực tiếp vào address book." }
      ]}
      outro={{ title: "Chia sẻ một lần.", italic: "Luôn được nhớ.", description: "Mọi kênh, mọi thiết bị — kết nối với một hồ sơ." }}
      afterFaq={<NetworkingToolsGrid />}
    />
  );
}

const TOOLS = [
  { title: "Form trao đổi liên hệ", desc: "Biến mỗi view thành lead được capture.", href: "/vi/platform/networking/lead-exchange", icon: UserPlus },
  { title: "Welcome Email", desc: "Tự gửi email chào branded ngay khi kết nối.", href: "/vi/platform/networking/welcome-email", icon: Mail },
  { title: "Apple & Google Wallet", desc: "Lưu thẻ vào wallet, luôn đồng bộ.", href: "/vi/platform/networking/wallet", icon: Wallet },
  { title: "Bảo vệ PIN", desc: "Khóa thẻ bằng PIN 4 chữ số cho chia sẻ riêng tư.", href: "/vi/platform/networking/pin-protection", icon: Lock },
  { title: "QR động", desc: "QR branded, tracked, re-routable.", href: "/vi/platform/networking/qr-code", icon: QrCode },
  { title: "Signature email", desc: "Signature đẹp cho Gmail, Outlook & Mail.", href: "/vi/platform/networking/email-signature", icon: Signature },
];

function NetworkingToolsGrid() {
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Công cụ networking</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Mọi kênh chia sẻ, <em className="italic">đều có.</em>
          </h2>
          <p className="mt-4 max-w-md text-base text-muted-foreground">
            Sáu công cụ chuyên biệt biến mỗi tap thành cuộc trò chuyện thật. Dùng một, dùng tất.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <RouterLink
                key={t.href}
                to={t.href}
                aria-label={`${t.title} — tìm hiểu thêm về công cụ networking này`}
                className="group flex flex-col rounded-2xl bg-muted p-6 transition hover:-translate-y-0.5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-bold">{t.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{t.desc}</p>
                <span className="mt-6 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Tìm hiểu thêm <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </span>
              </RouterLink>
            );
          })}
        </div>
      </div>
    </section>
  );
}